import { toStateCode } from './stateCodes';

export type AddressVerificationStatus =
  | 'unverified'
  | 'verified'
  | 'verified-warning'
  | 'needs-review';

export type TypedAddress = {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  /** ISO 3166-1 alpha-2/3, used only to relax the MX state-confirmation rule. */
  countryCode?: string;
};

function isMexico(countryCode: string | undefined) {
  const c = (countryCode ?? '').trim().toUpperCase();
  return c === 'MX' || c === 'MEX' || c === 'MEXICO';
}

/**
 * Google can never confirm MX state/province, so it is dropped from the
 * unconfirmed-components list rather than blocking on a gap that will never close.
 */
function filterMexicoStateUnconfirmed(
  rawUnconfirmedTypes: string[],
  countryCode: string | undefined,
): { unconfirmedTypes: string[]; stateUnconfirmedForMx: boolean } {
  const stateUnconfirmedForMx =
    isMexico(countryCode) &&
    rawUnconfirmedTypes.some(
      (t) => t.toLowerCase() === 'administrative_area_level_1',
    );
  const unconfirmedTypes = stateUnconfirmedForMx
    ? rawUnconfirmedTypes.filter(
        (t) => t.toLowerCase() !== 'administrative_area_level_1',
      )
    : rawUnconfirmedTypes;
  return { unconfirmedTypes, stateUnconfirmedForMx };
}

function buildBaseDetails(
  verdict: Record<string, unknown> | null,
  unconfirmedTypes: string[],
  stateUnconfirmedForMx: boolean,
): string[] {
  const details = stringList(verdict?.validationMessages);
  if (details.length === 0) {
    details.push(...unconfirmedTypes.map((t) => `Unconfirmed component: ${t}`));
  }
  if (stateUnconfirmedForMx) {
    details.push(
      'State/province could not be confirmed by the service for Mexico.',
    );
  }
  if (
    details.some((d) => /missing components:.*(route|street_number)/i.test(d))
  ) {
    details.push(
      'This location has no street number. Enter a street address — a landmark or intersection cannot be billed.',
    );
  }
  return details;
}

function computeFacts(
  payload: Record<string, unknown>,
  verdict: Record<string, unknown> | null,
  unconfirmedTypes: string[],
  stateUnconfirmedForMx: boolean,
): VerdictFacts {
  const facts = readFacts(payload, verdict, unconfirmedTypes);
  if (stateUnconfirmedForMx) {
    // Recompute from the filtered list rather than trusting Google's flag,
    // which still counts the state as unconfirmed.
    facts.hasUnconfirmed = unconfirmedTypes.length > 0;
  }
  return facts;
}

export type SuggestedAddress = {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  formatted: string;
};

export type AddressVerdictResult = {
  outcome: Exclude<AddressVerificationStatus, 'unverified'>;
  message: string;
  /** Problems with the address. */
  details: string[];
  /** Concrete edits the service made to what the user typed. */
  changes: string[];
  suggestion: SuggestedAddress | null;
};

/** Google granularities precise enough to bill and ship to. */
const PRECISE_GRANULARITIES = new Set([
  'SUB_PREMISE',
  'PREMISE',
  'PREMISE_PROXIMITY',
]);

const GRANULARITY_LABELS: Record<string, string> = {
  BLOCK: 'city block',
  ROUTE: 'street',
  OTHER: 'approximate area',
  GRANULARITY_UNSPECIFIED: 'unknown precision',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function text(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function unwrapAddressPayload(response: unknown): Record<string, unknown> {
  if (isRecord(response) && isRecord(response.result)) return response.result;
  return isRecord(response) ? response : {};
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : [];
}

function coordinate(
  payload: Record<string, unknown>,
  key: 'latitude' | 'longitude',
): number | null {
  if (typeof payload[key] === 'number') return payload[key];
  const location =
    isRecord(payload.geocode) && isRecord(payload.geocode.location)
      ? payload.geocode.location
      : null;
  return typeof location?.[key] === 'number' ? location[key] : null;
}

const UNIT_DESIGNATORS =
  '(?:apt|apartment|unit|ste|suite|rm|room|fl|floor|bldg|building|dept|lot|spc|space|trlr|#)\\.?\\s*';

/** Strips a trailing unit already held in line 2 so it is not duplicated. */
export function splitSuggestedStreet(street: string, typedLine2: string) {
  const unit = typedLine2.trim();
  if (!unit) return { line1: street, line2: '' };

  const escaped = unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // "123 Main St Apt 4" + line 2 "4": move the designator to line 2 so the
  // unit is not repeated in both fields.
  const withDesignator = new RegExp(
    `[,\\s]+(${UNIT_DESIGNATORS})${escaped}$`,
    'i',
  );
  const match = withDesignator.exec(street);
  if (match) {
    return {
      line1: street.replace(withDesignator, '').trim(),
      line2: `${match[1].trim()} ${unit}`.trim(),
    };
  }

  // A bare number can be part of the street itself ("Highway 5"), so only
  // strip units that carry non-numeric text.
  if (/^\d+[A-Za-z]?$/.test(unit)) return { line1: street, line2: unit };

  const pattern = new RegExp(`[,\\s]+${escaped}$`, 'i');
  return pattern.test(street)
    ? { line1: street.replace(pattern, '').trim(), line2: unit }
    : { line1: street, line2: unit };
}

function buildSuggestion(
  payload: Record<string, unknown>,
  typed: TypedAddress,
): SuggestedAddress | null {
  const validated = isRecord(payload.validatedAddress)
    ? payload.validatedAddress
    : null;
  if (!validated) return null;

  const split = splitSuggestedStreet(text(validated.address), typed.line2);
  const suggestion: SuggestedAddress = {
    line1: split.line1,
    line2: split.line2,
    city: text(validated.city),
    state: toStateCode(text(validated.state)),
    postalCode: text(validated.postalCode),
    formatted: text(validated.formattedAddress),
  };

  if (!suggestion.line1 && !suggestion.city && !suggestion.postalCode) {
    return null;
  }

  if (!suggestion.formatted) {
    suggestion.formatted = [
      [suggestion.line1, suggestion.line2].filter(Boolean).join(' '),
      suggestion.city,
      [suggestion.state, suggestion.postalCode].filter(Boolean).join(' '),
    ]
      .filter(Boolean)
      .join(', ');
  }

  return suggestion;
}

const same = (a: string, b: string) =>
  a.trim().toUpperCase() === b.trim().toUpperCase();

/** True when the ZIP only gained its +4 extension, e.g. 10010 → 10010-6907. */
function isZipPlusFourOf(suggested: string, typed: string) {
  return typed !== '' && suggested.startsWith(`${typed.trim()}-`);
}

/**
 * Hides a suggestion that adds nothing the user needs to act on. Google infers
 * a ZIP+4 on nearly every address, which would otherwise show a suggestion
 * every single time.
 */
function isSuggestionNoise(
  suggestion: SuggestedAddress,
  typed: TypedAddress,
): boolean {
  const streetSame = same(suggestion.line1, typed.line1);
  const line2Same = same(suggestion.line2, typed.line2);
  const citySame = same(suggestion.city, typed.city);
  const stateSame = same(suggestion.state, toStateCode(typed.state));
  if (!streetSame || !line2Same || !citySame || !stateSame) return false;

  return (
    same(suggestion.postalCode, typed.postalCode) ||
    isZipPlusFourOf(suggestion.postalCode, typed.postalCode)
  );
}

function diffLine(label: string, before: string, after: string) {
  const a = before.trim();
  const b = after.trim();
  if (!b || same(a, b)) return null;
  return a ? `${label}: “${a}” → “${b}”` : `${label} added: “${b}”`;
}

function buildChanges(
  suggestion: SuggestedAddress | null,
  typed: TypedAddress,
  hasInferred: boolean,
  hasReplaced: boolean,
): string[] {
  const changes: string[] = [];

  if (suggestion) {
    for (const entry of [
      diffLine('Street', typed.line1, suggestion.line1),
      diffLine('Unit', typed.line2, suggestion.line2),
      diffLine('City', typed.city, suggestion.city),
      diffLine('State', toStateCode(typed.state), suggestion.state),
      diffLine('ZIP', typed.postalCode, suggestion.postalCode),
    ]) {
      if (entry) changes.push(entry);
    }
  }

  if (changes.length === 0) {
    if (hasReplaced) changes.push('The service replaced part of this address.');
    else if (hasInferred)
      changes.push('The service filled in part of this address.');
  }

  return changes;
}

/**
 * Describes what a typeahead pick overwrote. Places will happily answer a
 * Boone NC query with a Pennsylvania address, so a silent overwrite is unsafe.
 */
export function describeSelectionChanges(
  before: Pick<TypedAddress, 'city' | 'state'>,
  after: Pick<TypedAddress, 'city' | 'state'>,
): string[] {
  const changes: string[] = [];
  const city = diffLine('City', before.city, after.city);
  const state = diffLine(
    'State',
    toStateCode(before.state),
    toStateCode(after.state),
  );
  if (before.city.trim() && city) changes.push(city);
  if (before.state.trim() && state) changes.push(state);
  return changes;
}

// Bare "US" / "CA" are omitted: they collide with state codes.
const COUNTRY_TOKENS = new Set([
  'USA',
  'UNITED STATES',
  'UNITED STATES OF AMERICA',
  'CANADA',
  'CAN',
  'MEXICO',
  'MEX',
  'MX',
]);
const US_ZIP = /^\d{5}(?:-\d{4})?$/;
const CA_POSTAL = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

export type SplitAddress = {
  line1: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode?: 'USA' | 'CAN' | 'MEX';
};

/**
 * Splits a whole address pasted into line 1 into its parts. Returns null unless
 * a trailing "STATE ZIP" is found, so ordinary street text is left untouched.
 */
export function splitPastedAddress(raw: string): SplitAddress | null {
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 3) return null;

  const rest = [...parts];
  const countryToken = rest[rest.length - 1].toUpperCase().replace(/\./g, '');
  const countryCode =
    countryToken === 'CANADA' || countryToken === 'CAN'
      ? 'CAN'
      : countryToken === 'MEXICO' ||
          countryToken === 'MEX' ||
          countryToken === 'MX'
        ? 'MEX'
        : COUNTRY_TOKENS.has(countryToken)
          ? 'USA'
          : undefined;
  if (countryCode) {
    rest.pop();
  }
  if (rest.length < 3) return null;

  const tail = rest[rest.length - 1].match(/^([A-Za-z.]{2,5})\s+(.+)$/);
  if (!tail) return null;

  const postalCode = tail[2].trim();
  if (!US_ZIP.test(postalCode) && !CA_POSTAL.test(postalCode)) return null;
  rest.pop();

  const city = rest.pop() ?? '';
  const line1 = rest.join(', ');
  if (!line1 || !city) return null;

  return {
    line1,
    city,
    state: toStateCode(tail[1]),
    postalCode,
    ...(countryCode ? { countryCode } : {}),
  };
}

type VerdictFacts = {
  hasCoordinates: boolean;
  isComplete: boolean;
  hasUnconfirmed: boolean;
  hasInferred: boolean;
  hasReplaced: boolean;
  possibleNextAction: string;
  validationStatus: string;
  granularity: string;
  unconfirmedTypes: string[];
};

function hasServiceChangesWithoutSuggestion(
  validated: SuggestedAddress | null,
  facts: Pick<VerdictFacts, 'hasInferred' | 'hasReplaced'>,
) {
  return !validated && (facts.hasInferred || facts.hasReplaced);
}

function readFacts(
  payload: Record<string, unknown>,
  verdict: Record<string, unknown> | null,
  unconfirmedTypes: string[],
): VerdictFacts {
  return {
    hasCoordinates:
      coordinate(payload, 'latitude') != null &&
      coordinate(payload, 'longitude') != null,
    isComplete: Boolean(verdict?.isComplete ?? verdict?.addressComplete),
    hasUnconfirmed:
      Boolean(verdict?.hasUnconfirmedComponents) || unconfirmedTypes.length > 0,
    hasInferred: Boolean(verdict?.hasInferredComponents),
    hasReplaced: Boolean(verdict?.hasReplacedComponents),
    possibleNextAction: String(verdict?.possibleNextAction ?? ''),
    validationStatus: String(verdict?.validationStatus ?? ''),
    granularity: String(verdict?.geocodeGranularity ?? '').toUpperCase(),
    unconfirmedTypes,
  };
}

function isImprecise(granularity: string) {
  return granularity !== '' && !PRECISE_GRANULARITIES.has(granularity);
}

/**
 * An imprecise geocode only blocks when the service also failed to confirm the
 * address. Confirmed PO boxes legitimately return OTHER granularity.
 */
function isBlockingImprecision(facts: VerdictFacts, typed: TypedAddress) {
  if (!isImprecise(facts.granularity)) return false;
  const isConfirmedPoBox =
    /\bp\.?\s*o\.?\s*box\b|\bpost\s+office\s+box\b/i.test(
      `${typed.line1} ${typed.line2}`,
    ) &&
    facts.validationStatus === 'Confirmed' &&
    facts.isComplete;
  return !isConfirmedPoBox;
}

function needsReview(facts: VerdictFacts, typed: TypedAddress) {
  return (
    !facts.hasCoordinates ||
    !facts.isComplete ||
    isBlockingImprecision(facts, typed) ||
    (facts.validationStatus === 'PartialMatch' &&
      facts.possibleNextAction !== 'ACCEPT') ||
    (facts.hasUnconfirmed && facts.possibleNextAction !== 'ACCEPT')
  );
}

export function evaluateAddressResponse(
  response: unknown,
  typed: TypedAddress,
): AddressVerdictResult {
  const payload = unwrapAddressPayload(response);
  const verdict = isRecord(payload.verdict) ? payload.verdict : null;
  const address = isRecord(payload.address) ? payload.address : null;

  const rawUnconfirmedTypes = stringList(address?.unconfirmedComponentTypes);
  const { unconfirmedTypes, stateUnconfirmedForMx } =
    filterMexicoStateUnconfirmed(rawUnconfirmedTypes, typed.countryCode);

  const details = buildBaseDetails(
    verdict,
    unconfirmedTypes,
    stateUnconfirmedForMx,
  );

  const facts = computeFacts(
    payload,
    verdict,
    unconfirmedTypes,
    stateUnconfirmedForMx,
  );
  const validated = buildSuggestion(payload, typed);
  const suggestion =
    validated && isSuggestionNoise(validated, typed) ? null : validated;
  const hasGenericServiceChanges = hasServiceChangesWithoutSuggestion(
    validated,
    facts,
  );
  const changes = buildChanges(
    suggestion,
    typed,
    !validated && facts.hasInferred,
    !validated && facts.hasReplaced,
  );

  // The service relocating the address to another state means it matched
  // something else entirely; never present that as verified.
  const relocated = Boolean(
    validated &&
    typed.state.trim() &&
    validated.state &&
    !same(validated.state, toStateCode(typed.state)),
  );
  if (relocated && validated) {
    details.push(
      `Matched an address in ${validated.state}, not ${toStateCode(typed.state)}.`,
    );
  }

  if (isImprecise(facts.granularity)) {
    const label =
      GRANULARITY_LABELS[facts.granularity] ??
      facts.granularity.toLowerCase().replace(/_/g, ' ');
    details.push(
      `Location matched only to ${label} level, not a specific building.`,
    );
  }

  if (relocated || needsReview(facts, typed)) {
    return {
      outcome: 'needs-review',
      message:
        'Address could not be fully verified. Review the details below, or use the suggested address.',
      details,
      changes,
      suggestion: relocated ? validated : suggestion,
    };
  }

  const subpremiseOnlyUnconfirmed =
    facts.hasUnconfirmed &&
    unconfirmedTypes.length > 0 &&
    unconfirmedTypes.every((t) => t.toLowerCase() === 'subpremise');

  if (subpremiseOnlyUnconfirmed) {
    return {
      outcome: 'verified-warning',
      message:
        'Address verified. The unit/suite could not be confirmed, so mail may not reach the right desk.',
      details:
        details.length > 0
          ? details
          : ['Unit/suite detail is plausible but unconfirmed.'],
      changes,
      suggestion,
    };
  }

  if (hasGenericServiceChanges || changes.length > 0) {
    return {
      outcome: 'verified-warning',
      message:
        !validated && facts.hasReplaced
          ? 'Address verified, but the service changed what you entered. Review the changes below.'
          : 'Address verified. The service filled in missing detail — review the changes below.',
      details,
      changes,
      suggestion,
    };
  }

  if (isImprecise(facts.granularity)) {
    return {
      outcome: 'verified-warning',
      message:
        'Address confirmed, but it does not point at a specific building. Check it is billable.',
      details,
      changes,
      suggestion,
    };
  }

  return {
    outcome: 'verified',
    message: 'Address verified by service.',
    details: [],
    changes: [],
    suggestion,
  };
}
